import { clsx } from 'clsx';
import Fuse from 'fuse.js';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { getFirstEdit, type Revision, type RevisionReusableContent } from '@gitbook/hive-core';
import { useHiveRevision } from '@gitbook/hive-react';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Description,
    DialogRoot,
    EmptyState,
    EmptyStateActions,
    EmptyStateIcon,
    EmptyStateIllustration,
    List,
    type ListRenderItemInfo,
    ListSeparator,
    ProgressiveImage,
    TextInput,
    useDialogTriggerState,
    usePaginatedList,
} from '@gitbook/spine-react';

import { CreateReusableContentDialog } from './CreateReusableContentDialog';
import emptyImageDarkSrc from './images/empty-dark.svg';
import emptyImageLightSrc from './images/empty-light.svg';
import { ReusableContentItem } from './ReusableContentItem';
import { useReusableContentDisclaimer } from './useReusableContentDisclaimer';

interface ReusableContentsManagerProps {
    space: api.Space;
    className?: string;
}

/**
 * Sidebar with a list of reusable contents.
 */
export function ReusableContentsManager(props: ReusableContentsManagerProps) {
    const { space, className } = props;

    // We use the source revision to avoid displaying computed reusable contents
    // as the user can't edit them.
    // We might want to display computed files in the future, as it's technically possible
    // for the user to insert them in a non computed page.
    const { sourceRevision: revision } = useHiveRevision();
    const createDialogTrigger = useDialogTriggerState();

    const onCreate = () => {
        createDialogTrigger.open();
    };

    return (
        <>
            <DialogRoot {...createDialogTrigger.rootProps}>
                <CreateReusableContentDialog
                    onInsert={() => {
                        analytics.logEvent(
                            analytics.createSpaceEvent(space, {
                                type: 'reusable_content_create',
                                method: 'sidebar',
                            })
                        );
                    }}
                />
            </DialogRoot>
            {Object.keys(revision.reusableContents).length === 0 ? (
                <ReusableContentsEmptyState onCreate={onCreate} />
            ) : (
                <ReusableContentsList
                    space={space}
                    revision={revision}
                    onCreate={onCreate}
                    className={className}
                />
            )}
        </>
    );
}

function ReusableContentsEmptyState(props: { onCreate: () => void }) {
    const { onCreate } = props;
    const startWithReusable = useReusableContentDisclaimer();

    return (
        <EmptyState>
            <EmptyStateIllustration>
                <ProgressiveImage
                    width={72}
                    height={64}
                    src={{
                        light: emptyImageLightSrc,
                        dark: emptyImageDarkSrc,
                    }}
                />
            </EmptyStateIllustration>
            <Description>Create and reuse blocks of content across your documentation.</Description>
            <EmptyStateActions>
                <Button
                    leadingIcon={Icon.Plus}
                    kind="secondary"
                    label="Create reusable content"
                    onPress={() => {
                        startWithReusable({
                            mode: 'create',
                            callback: onCreate,
                        });
                    }}
                />
            </EmptyStateActions>
        </EmptyState>
    );
}

function ReusableContentsList(props: {
    space: api.Space;
    revision: Revision;
    onCreate: () => void;
    className?: string;
}) {
    const { space, revision, onCreate, className } = props;

    const [query, setQuery] = React.useState('');

    const reusableContents = React.useMemo(() => {
        // Sort them by order of creation to get a predictable order (recents first)
        const all = Object.values(revision.reusableContents).sort((a, b) => {
            return (
                (getFirstEdit(b.edits)?.updatedAt ?? 0) - (getFirstEdit(a.edits)?.updatedAt ?? 0)
            );
        });

        if (query) {
            const fuse = new Fuse(all, {
                shouldSort: true,
                threshold: 0.6,
                distance: 100,
                minMatchCharLength: 1,
                keys: ['title'],
            });

            return fuse.search(query).map((x) => x.item);
        }

        return all;
    }, [revision, query]);

    const list = usePaginatedList({
        label: 'content',
        items: reusableContents,
        itemsPerPage: 15,
    });

    /** Callback to render a file in the list */
    const renderItem = React.useCallback(
        ({ item }: ListRenderItemInfo<RevisionReusableContent>) => (
            <ReusableContentItem
                space={space}
                reusableContent={item}
                selected={false}
                query={query}
            />
        ),
        [query, space]
    );

    return (
        <div className={clsx('flex flex-1 flex-col gap-4', className)}>
            <div className="flex gap-2">
                <TextInput
                    autoFocus
                    leadingIcon={Icon.Search}
                    placeholder="Search reusable content"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                />

                <Button
                    iconOnly
                    kind="secondary"
                    label="Create new reusable content"
                    leadingIcon={Icon.Plus}
                    onPress={() => {
                        onCreate();
                    }}
                />
            </div>
            {list.items.length === 0 ? (
                <EmptyState className="flex-1">
                    <EmptyStateIcon>
                        <Icon.ReusableContent />
                    </EmptyStateIcon>
                    <Description>No reusable content found.</Description>
                </EmptyState>
            ) : (
                <List
                    ref={list.ref}
                    items={list.items}
                    renderItem={renderItem}
                    separator={ListSeparator}
                />
            )}
        </div>
    );
}
